import { rqlBuilder } from '@extrahorizon/javascript-sdk';
import { SCHEMA_NAMES, sdk } from 'utils/query';
import { WeightMeasurement } from 'models/weight';
import dayjs from 'dayjs';
import { chunk } from 'lodash';
import { ConcurrentQueue, DEFAULT_CONCURRENCY_LIMIT } from 'utils/concurrentQueue';
import { EmptyParametersError } from 'services/errors/EmptyParametersError';
import { NoMeasurementsError } from 'services/errors/NoMeasurementsError';
import { PMDBMIData } from 'models/patientMedicalData';

const MAX_GROUP_BATCH_SIZE = 5;

interface GetWeightMeasurementsParams {
  patientId?: string;
  groupIds?: string[];
  startDate?: Date;
  endDate?: Date;
}

export const getWeightMeasurements = async (params: GetWeightMeasurementsParams): Promise<WeightMeasurement[]> => {
  const { patientId, groupIds, startDate, endDate } = params;
  if (!patientId || !groupIds || !startDate || !endDate) throw new EmptyParametersError();

  if (groupIds.length === 0) return [];

  const allMeasurements: WeightMeasurement[] = [];

  const queue = new ConcurrentQueue<WeightMeasurement[]>({
    concurrencyLimit: DEFAULT_CONCURRENCY_LIMIT,
    onComplete: async (measurements) => {
      allMeasurements.push(...measurements);
    },
  });

  const groupIdBatches = chunk(groupIds, MAX_GROUP_BATCH_SIZE);

  const tasks = groupIdBatches.map((batchedGroupIds) =>
    queue.push(async () => {
      const rql = rqlBuilder()
        .eq('data.patientId', patientId)
        .in('groupIds', batchedGroupIds)
        .ge('data.recordedTimestamp', `${dayjs(startDate).startOf('day').toISOString()}`)
        .le('data.recordedTimestamp', `${dayjs(endDate).endOf('day').toISOString()}`)
        .sort('data.recordedTimestamp')
        .build();

      return sdk.data.documents.findAll<PMDBMIData>(SCHEMA_NAMES.patientMedicalData, { rql });
    }),
  );

  await Promise.all(tasks);

  return allMeasurements.sort(
    (a, b) => new Date(a.data.recordedTimestamp).getTime() - new Date(b.data.recordedTimestamp).getTime(),
  );
};

export interface GetWeightMeasurementDetailsParams {
  id?: string;
}

export const getWeightMeasurementById = async (
  params: GetWeightMeasurementDetailsParams,
): Promise<WeightMeasurement> => {
  const { id } = params;
  if (!id) throw new EmptyParametersError();

  const rql = rqlBuilder().eq('id', id).build();

  const result = await sdk.data.documents.findFirst<PMDBMIData>(SCHEMA_NAMES.patientMedicalData, {
    rql,
  });
  if (!result) throw new NoMeasurementsError();

  return result;
};
